import type {
  ChatMessage,
  GameStatsResponse,
  GameSummary,
  GameView,
  LeaderboardRow,
  MeResponse,
  SoundInfo,
  SoundLibraryData,
  PlayerProfile,
  TankLook,
  TurnInput,
  TurnsResponse,
  UserInfo,
} from '@tanky/shared';
import { setServerNow } from './clock';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('Nelze se spojit se serverem.', 0);
  }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(data.error ?? `Chyba ${r.status}`, r.status);
  if (typeof data.serverNow === 'number') setServerNow(data.serverNow);
  else if (typeof data.game?.serverNow === 'number') setServerNow(data.game.serverNow);
  return data as T;
}

export const api = {
  me: () => req<MeResponse>('GET', '/auth/me'),
  sounds: () => req<SoundLibraryData>('GET', '/sounds'),
  soundUrl: (id: string, version?: number) =>
    `/api/sounds/${encodeURIComponent(id)}/file${version ? `?v=${version}` : ''}`,
  renameSound: (id: string, name: string) => req('PATCH', `/sounds/${encodeURIComponent(id)}`, { name }),
  deleteSound: (id: string) => req('DELETE', `/sounds/${encodeURIComponent(id)}`),
  setBinding: (action: string, items: { soundId: string; volume: number }[]) => req('PUT', '/sounds/bindings', { action, items }),
  /** Nahraje soubor zvuku (surová data); kanály a délku zjistí prohlížeč. */
  uploadSound: async (file: Blob, name: string, channels: number, duration: number, fileName?: string): Promise<SoundInfo> => {
    const fn = fileName ?? (file instanceof File ? file.name : undefined);
    const q = new URLSearchParams({ name, channels: String(channels), duration: duration.toFixed(3) });
    if (fn) q.set('fileName', fn);
    let r: Response;
    try {
      r = await fetch(`/api/sounds?${q}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new ApiError('Nelze se spojit se serverem.', 0);
    }
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new ApiError(data.error ?? `Chyba ${r.status}`, r.status);
    return data.sound as SoundInfo;
  },
  /** Nahradí audio soubor existujícího zvuku. */
  replaceSoundFile: async (id: string, file: Blob, channels: number, duration: number, fileName?: string): Promise<SoundInfo> => {
    const fn = fileName ?? (file instanceof File ? file.name : undefined);
    const q = new URLSearchParams({ channels: String(channels), duration: duration.toFixed(3) });
    if (fn) q.set('fileName', fn);
    let r: Response;
    try {
      r = await fetch(`/api/sounds/${encodeURIComponent(id)}/file?${q}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new ApiError('Nelze se spojit se serverem.', 0);
    }
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new ApiError(data.error ?? `Chyba ${r.status}`, r.status);
    return data.sound as SoundInfo;
  },
  leaderboard: () => req<{ rows: LeaderboardRow[] }>('GET', '/stats'),
  playerStats: (id: string) => req<PlayerProfile>('GET', `/stats/${encodeURIComponent(id)}`),
  gameStats: (id: string) => req<GameStatsResponse>('GET', `/games/${encodeURIComponent(id)}/stats`),
  addBot: (id: string, level: string) => req<{ game: GameSummary }>('POST', `/games/${encodeURIComponent(id)}/bots`, { level }),
  removeBot: (id: string, botId: string) =>
    req('DELETE', `/games/${encodeURIComponent(id)}/bots/${encodeURIComponent(botId)}`),
  uploadEmblem: (dataUrl: string) => req<{ look: TankLook }>('PUT', '/me/emblem', { dataUrl }),
  deleteEmblem: () => req<{ look: TankLook }>('DELETE', '/me/emblem'),
  saveLook:(look: TankLook) => req<{ look: TankLook }>('PUT', '/me/tank', look),
  chat: (id: string, text: string, bubble: boolean) =>
    req<{ msg: ChatMessage }>('POST', `/games/${encodeURIComponent(id)}/chat`, { text, bubble }),
  login: (name: string, password: string) => req<{ user: UserInfo }>('POST', '/auth/login', { name, password }),
  register: (name: string, password: string, code?: string) =>
    req<{ user: UserInfo }>('POST', '/auth/register', { name, password, code }),
  logout: () => req('POST', '/auth/logout', {}),
  games: () => req<{ games: GameSummary[] }>('GET', '/games'),
  createGame: (settings: unknown) => req<{ game: GameSummary }>('POST', '/games', settings),
  join: (code: string) => req<{ game: GameSummary }>('POST', '/games/join', { code }),
  game: (id: string) => req<{ game: GameView }>('GET', `/games/${encodeURIComponent(id)}`),
  turns: (id: string, since: number) => req<TurnsResponse>('GET', `/games/${encodeURIComponent(id)}/turns?since=${since}`),
  start: (id: string) => req<{ game: GameView }>('POST', `/games/${encodeURIComponent(id)}/start`, {}),
  leave: (id: string) => req('POST', `/games/${encodeURIComponent(id)}/leave`, {}),
  turn: (id: string, input: TurnInput) => req<{ seq: number }>('POST', `/games/${encodeURIComponent(id)}/turn`, input),
  drive: (id: string, move: number) => req<{ seq: number }>('POST', `/games/${encodeURIComponent(id)}/drive`, { move }),
  surrender: (id: string) => req('POST', `/games/${encodeURIComponent(id)}/surrender`, {}),
  rematch: (id: string) => req<{ game: GameSummary }>('POST', `/games/${encodeURIComponent(id)}/rematch`, {}),
};
